import React, { useEffect, useState } from 'react';
import apiService from '../../../services/apiService';
import CustomerMessages from './CustomerMessages';
import SLAAlertBanner from './SLAAlertBanner';


const STANDARD_TEMPLATES: Record<string, string[]> = {
  'Booking': [
    'Collect booking amount receipt',
    'Verify customer photo & details',
    'Generate Booking Order form'
  ],
  'Document Complete': [
    'PAN CARD',
    'AADHAR CARD',
    'UDYAM CERTIFICATE',
    'DRIVING LICENSE',
    'LIGHT BILL',
    'RENT AGREEMENT',
    'ITR',
    '13 MONTH BANK STATEMENT',
    '8 PHOTO',
    '6 CHEQUE ( FOR BANK SECURITY )'
  ],
  'Loan Login': [
    'Submit files to bank partner',
    'Verify CIBIL score status',
    'Obtain Loan Sanction Letter'
  ],
  'Verification': [
    'Schedule physical home visit',
    'Schedule physical office visit',
    'Upload verification report'
  ],
  'Account Opening': [
    'Fill account opening form',
    'Complete customer KYC verification',
    'Issue welcome kit / checkbook'
  ],
  'Agreement': [
    'Draft loan agreement copy',
    'Get signatures from customer & bank',
    'Stamp paper execution check'
  ],
  'Down Payment': [
    'Receive down payment clear receipt',
    'Confirm receipt with accounts desk',
    'Generate tax invoice copy'
  ],
  'Disbursement': [
    'Submit delivery challan to bank',
    'Confirm disbursement payment credited',
    'Issue gate pass clearance'
  ],
  'RTO (Passing)': [
    'Submit tax and registration form',
    'Arrange physical passing inspection',
    'Fit High-Security Registration Plate (HSRP)'
  ],
  'Delivery': [
    'Arrange final vehicle polish & wash',
    'Conduct vehicle delivery ceremony',
    'Collect signed Delivery Acknowledgement form'
  ]
};

interface DepartmentDashboardProps {
  userRole: string;
  workflowStep: string; // e.g. 'Bank Logging', 'RTO Processing'
}

const DepartmentDashboard: React.FC<DepartmentDashboardProps> = ({ userRole, workflowStep }) => {
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState('Done');
  const [bulkReason, setBulkReason] = useState('');
  const [saving, setSaving] = useState<any>(false); // Can be boolean or stepId string
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [selectedCustomerMessages, setSelectedCustomerMessages] = useState<any>(null);

  // Search & Filters state
  const [search, setSearch] = useState('');

  // Status Update Modal state
  const [activeUpdateModal, setActiveUpdateModal] = useState<{
    stepId: string;
    stepName: string;
    customerName: string;
    currentStatus: string;
    currentReason?: string;
  } | null>(null);
  const [modalStatus, setModalStatus] = useState<string>('');
  const [modalReason, setModalReason] = useState<string>('');

  // Checklist Modal States
  const [checklistModalStep, setChecklistModalStep] = useState<any>(null);
  const [checklists, setChecklists] = useState<any[]>([]);
  const [newChecklistTask, setNewChecklistTask] = useState('');
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  const { userId, userName } = React.useMemo(() => {
    try {
      const userStr = sessionStorage.getItem('aswCrmUser') || sessionStorage.getItem('aswUser') || localStorage.getItem('aswCrmUser') || localStorage.getItem('aswUser');
      const parsed = userStr ? JSON.parse(userStr) : null;
      return { userId: parsed?.id || null, userName: parsed?.name || null };
    } catch { return { userId: null, userName: null }; }
  }, []);

  const fetchCustomers = React.useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await apiService.getCrmCustomers(workflowStep);
      if (res.success) setCustomers(res.data);
    } catch (e) { console.error(e); }
    finally { if (!silent) setLoading(false); }
  }, [workflowStep]);

  useEffect(() => { 
    fetchCustomers(false); 
  }, [fetchCustomers]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden) {
        fetchCustomers(true);
      }
    }, 7200000); // 2 hours instead of 30 seconds to prevent database egress exhaustion
    return () => clearInterval(interval);
  }, [fetchCustomers]);

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === customers.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(customers.map((c: any) => c.myStep?.id)));
    }
  };

  const handleBulkUpdate = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    try {
      await Promise.all(
        Array.from(selected).map(async stepId => {
          // 1. Update the actual workflow step
          await apiService.updateCrmWorkflowStep(stepId, {
            status: bulkStatus,
            reason: bulkReason,
            updated_by: userId
          });

          // 2. Post the reason to the customer's message thread
          const currCustomer = customers.find(c => c.myStep?.id === stepId);
          if (currCustomer && bulkReason.trim()) {
            await apiService.logCrmActivity({
              action_type: 'staff_update',
              entity_type: 'customer',
              entity_id: currCustomer.id,
              description: `[${bulkStatus}] ${bulkReason}`,
              performed_by: userId
            });
          }
        })
      );
      setSavedIds(new Set(selected));
      setSelected(new Set());
      setBulkReason('');
      fetchCustomers();
      setTimeout(() => setSavedIds(new Set()), 2500);
    } catch (e) { alert('Error updating steps'); }
    finally { setSaving(false); }
  };

  // 1. Customer search filter
  const filteredCustomers = customers.filter(c => {
    if (search) {
      const q = search.toLowerCase();
      if (!c.customer_name?.toLowerCase().includes(q) && !c.phone?.includes(q)) {
        return false;
      }
    }
    return true;
  });

  // 2. Open update modal handler
  const openUpdateModal = (step: any, customer: any) => {
    setActiveUpdateModal({
      stepId: step.id,
      stepName: step.step_name,
      customerName: customer.customer_name,
      currentStatus: step.status,
      currentReason: step.reason || ''
    });
    setModalStatus(step.status);
    setModalReason(step.reason || '');
  };

  // 3. Submit update modal handler
  const handleModalSubmit = async () => {
    if (!activeUpdateModal) return;
    const { stepId } = activeUpdateModal;
    setSaving(stepId);

    // Optimistically update local state for instant UI feedback
    setCustomers(prev => prev.map(cust => {
      if (cust.myStep?.id === stepId) {
        return {
          ...cust,
          myStep: { ...cust.myStep, status: modalStatus, reason: modalStatus === 'Done' ? null : modalReason || cust.myStep.reason }
        };
      }
      return cust;
    }));

    try {
      const res = await apiService.updateCrmWorkflowStep(stepId, { 
        status: modalStatus, 
        reason: modalStatus === 'Done' ? null : modalReason || undefined,
        updated_by: userId 
      });
      if (res.success) {
        const currCustomer = customers.find(c => c.myStep?.id === stepId);
        if (currCustomer && modalReason.trim() && modalStatus !== 'Done') {
          await apiService.logCrmActivity({
            action_type: 'staff_update',
            entity_type: 'customer',
            entity_id: currCustomer.id,
            description: `[${modalStatus}] ${modalReason}`,
            performed_by: userId
          });
        }
        fetchCustomers(true);
      } else {
        alert(res.message || 'Failed to update status');
        fetchCustomers();
      }
    } catch (err: any) {
      console.error(err);
      alert('Failed to update status: ' + (err.message || 'Server error'));
      fetchCustomers();
    } finally {
      setSaving(false);
      setActiveUpdateModal(null);
    }
  };

  // 4. Checklist effect hook
  useEffect(() => {
    if (checklistModalStep) {
      apiService.getStepChecklist(checklistModalStep.id)
        .then((res: any) => {
          if (res.success) {
            const items = res.data || [];
            setChecklists(items);
            // Auto-load 10 document checklist if Document Complete step is opened with no items
            if (items.length === 0 && checklistModalStep.step_name === 'Document Complete') {
              const defaultTasks = STANDARD_TEMPLATES['Document Complete'];
              Promise.all(defaultTasks.map(t => apiService.addChecklistItem(checklistModalStep.id, t)))
                .then((results) => {
                  const added = results.filter((r: any) => r.success).map((r: any) => r.data);
                  setChecklists(added);
                })
                .catch((err: any) => console.error('Error auto-loading document template:', err));
            }
          }
        })
        .catch((err: any) => console.error(err));
    } else {
      setChecklists([]);
    }
  }, [checklistModalStep]);

  // 5. Checklist action handlers
  const handleToggleChecklist = async (id: string, currentVal: boolean) => {
    try {
      const res = await apiService.toggleChecklistItem(id, !currentVal);
      if (res.success) {
        setChecklists(prev => prev.map(item => item.id === id ? { ...item, is_completed: !currentVal } : item));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddChecklist = async () => {
    if (!newChecklistTask.trim() || !checklistModalStep) return;
    try {
      const res = await apiService.addChecklistItem(checklistModalStep.id, newChecklistTask.trim());
      if (res.success) {
        setChecklists(prev => [...prev, res.data]);
        setNewChecklistTask('');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleLoadTemplate = async () => {
    if (!checklistModalStep) return;
    const tasks = STANDARD_TEMPLATES[checklistModalStep.step_name] || [];
    if (tasks.length === 0) return;

    setLoadingTemplate(true);
    try {
      const promises = tasks.map(t => apiService.addChecklistItem(checklistModalStep.id, t));
      const results = await Promise.all(promises);
      const addedItems = results.filter(r => r.success).map(r => r.data);
      setChecklists(prev => [...prev, ...addedItems]);
    } catch (e) {
      console.error('Error loading templates', e);
    } finally {
      setLoadingTemplate(false);
    }
  };

  const statusColors: Record<string, string> = {
    'Pending': '#FEF3C7',
    'Hold': '#FEE2E2',
    'Done': '#D1FAE5',
  };
  const statusText: Record<string, string> = {
    'Pending': '#92400E',
    'Hold': '#991B1B',
    'Done': '#065F46',
  };

  if (loading) {
    return <div className="crm-loading"><div className="crm-spinner"></div> Loading your assigned customers...</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="crm-page-header">
        <div className="crm-page-header-left">
          <h2>{workflowStep} Dashboard</h2>
          <p>
            {customers.length === 0
              ? 'No customers pending at your stage'
              : `${customers.length} customer${customers.length !== 1 ? 's' : ''} need your action`}
            {userName && <span style={{ color: 'var(--crm-text-muted)' }}> · logged in as {userName}</span>}
          </p>
        </div>
        <button className="crm-btn crm-btn-ghost" onClick={() => fetchCustomers(false)}>↻ Refresh</button>
      </div>

      {/* SLA Alert Banner (Department specific: only shows alerts for this workflowStep) */}
      <SLAAlertBanner customers={customers} departmentStep={workflowStep} slaDays={3} />

      {/* KPI Metric Cards Widget */}
      {(() => {
        const totalCases = customers.length;
        const pendingCases = customers.filter(c => c.myStep?.status === 'Pending').length;
        const holdCases = customers.filter(c => c.myStep?.status === 'Hold').length;
        const slaWarningCases = customers.filter(c => 
          c.myStep?.status !== 'Done' && 
          c.myStep?.updated_at && 
          (Date.now() - new Date(c.myStep.updated_at).getTime()) > 3 * 24 * 60 * 60 * 1000
        ).length;

        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '20px' }}>
            <div style={{ background: 'white', border: '1.5px solid var(--crm-border)', borderRadius: 'var(--crm-radius)', padding: '16px 20px', boxShadow: 'var(--crm-shadow)' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--crm-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📁 Total Cases</div>
              <div style={{ fontSize: '28px', fontWeight: 800, color: 'var(--crm-primary)', marginTop: '8px' }}>{totalCases}</div>
            </div>
            <div style={{ background: 'white', border: '1.5px solid var(--crm-border)', borderRadius: 'var(--crm-radius)', padding: '16px 20px', boxShadow: 'var(--crm-shadow)' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--crm-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>⏳ Pending Actions</div>
              <div style={{ fontSize: '28px', fontWeight: 800, color: '#D97706', marginTop: '8px' }}>{pendingCases}</div>
            </div>
            <div style={{ background: 'white', border: '1.5px solid var(--crm-border)', borderRadius: 'var(--crm-radius)', padding: '16px 20px', boxShadow: 'var(--crm-shadow)' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--crm-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>⏸️ On Hold</div>
              <div style={{ fontSize: '28px', fontWeight: 800, color: '#EF4444', marginTop: '8px' }}>{holdCases}</div>
            </div>
            <div style={{ background: slaWarningCases > 0 ? '#FEF2F2' : 'white', border: `1.5px solid ${slaWarningCases > 0 ? '#FCA5A5' : 'var(--crm-border)'}`, borderRadius: 'var(--crm-radius)', padding: '16px 20px', boxShadow: 'var(--crm-shadow)' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: slaWarningCases > 0 ? '#DC2626' : 'var(--crm-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🚨 SLA Warnings (3d+)</div>
              <div style={{ fontSize: '28px', fontWeight: 800, color: slaWarningCases > 0 ? '#DC2626' : 'var(--crm-text-muted)', marginTop: '8px' }}>{slaWarningCases}</div>
            </div>
          </div>
        );
      })()}

      {/* Bulk Action Bar */}
      {customers.length > 0 && (
        <div style={{
          display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap',
          padding: '12px 16px', background: selected.size > 0 ? 'var(--crm-primary-light)' : 'var(--crm-bg)',
          border: '1.5px solid var(--crm-border)', borderRadius: 'var(--crm-radius)', marginBottom: 16, transition: 'all 0.2s'
        }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--crm-text-primary)', minWidth: 120 }}>
            {selected.size > 0 ? `${selected.size} selected` : 'Bulk Update'}
          </span>
          <select
            value={bulkStatus}
            onChange={e => setBulkStatus(e.target.value)}
            style={{ padding: '7px 10px', border: '1.5px solid var(--crm-border)', borderRadius: '6px', fontSize: '13px' }}
          >
            <option value="Done">✅ Done</option>
            <option value="Pending">⏳ Pending</option>
            <option value="Hold">🔴 Hold</option>
            <option value="Cancelled">❌ Booking Cancelled</option>
          </select>
          {(bulkStatus === 'Hold' || bulkStatus === 'Pending' || bulkStatus === 'Cancelled') && (
            <input
              type="text"
              placeholder="Reason (optional)..."
              value={bulkReason}
              onChange={e => setBulkReason(e.target.value)}
              style={{ flex: 1, minWidth: '150px', padding: '7px 11px', border: '1.5px solid var(--crm-border)', borderRadius: '6px', fontSize: '13px' }}
            />
          )}
          <button
            className="crm-btn crm-btn-success"
            onClick={handleBulkUpdate}
            disabled={selected.size === 0 || saving}
          >
            {saving ? 'Updating…' : `✓ Update ${selected.size > 0 ? selected.size : ''} Selected`}
          </button>
        </div>
      )}

      {/* Search Bar Toolbar */}
      {customers.length > 0 && (
        <div style={{
          display: 'flex',
          gap: '10px',
          background: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          border: '1px solid var(--crm-border)',
          marginBottom: '16px',
          alignItems: 'center'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            background: '#F8FAFC', 
            border: '1px solid var(--crm-border)', 
            borderRadius: '6px', 
            padding: '0 10px', 
            flex: '1 1 300px', 
            maxWidth: '400px' 
          }}>
            <span style={{ marginRight: '6px', color: 'var(--crm-text-muted)', fontSize: '13px' }}>🔍</span>
            <input
              type="text"
              placeholder="Search pending customers by name or phone..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ 
                flex: 1, 
                border: 'none', 
                outline: 'none', 
                fontSize: '13px', 
                padding: '8px 0', 
                background: 'transparent',
                color: 'var(--crm-text-primary)'
              }}
            />
            {search && (
              <button 
                onClick={() => setSearch('')} 
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--crm-text-muted)', fontSize: '14px' }}
              >
                ×
              </button>
            )}
          </div>
          <div style={{ marginLeft: 'auto', fontSize: '13px', color: 'var(--crm-text-muted)', fontWeight: 500 }}>
            {filteredCustomers.length} customer{filteredCustomers.length !== 1 ? 's' : ''} found
          </div>
        </div>
      )}

      {/* Customer Table */}
      {customers.length === 0 ? (
        <div className="crm-empty-state">
          <div className="crm-empty-state-icon">✅</div>
          <h3>All caught up!</h3>
          <p>No customers are waiting at the <strong>{workflowStep}</strong> stage right now.</p>
        </div>
      ) : filteredCustomers.length === 0 ? (
        <div className="crm-empty-state" style={{ padding: '40px 20px', background: 'white', border: '1px solid var(--crm-border)', borderRadius: '8px' }}>
          <h3>No matching customers</h3>
          <p>Try clearing or adjusting your search filter.</p>
          <button className="crm-btn crm-btn-ghost" style={{ marginTop: 12 }} onClick={() => setSearch('')}>
            Reset Search
          </button>
        </div>
      ) : (
        <div className="crm-table-wrapper">
          <table className="crm-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input type="checkbox" checked={selected.size === filteredCustomers.length} onChange={toggleAll} title="Select all" />
                </th>
                <th>Customer</th>
                <th>Phone</th>
                <th>Car Details</th>
                <th>Current Status</th>
                <th>Remarks / Tasks</th>
                <th>Last Updated</th>
                <th>Quick Update</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((c: any) => {
                const step = c.myStep;
                if (!step) return null;
                const isSaved = savedIds.has(step.id);

                // Stuck / Duration check
                let durationText = '';
                let isSlaBreached = false;
                if (step.status !== 'Done') {
                  const stepTime = step.updated_at || c.created_at;
                  const diffMs = Date.now() - new Date(stepTime).getTime();
                  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                  if (diffDays > 0) {
                    durationText = `${diffDays}d elapsed`;
                    if (diffDays >= 3) {
                      isSlaBreached = true;
                    }
                  } else if (diffHours > 0) {
                    durationText = `${diffHours}h elapsed`;
                  } else {
                    durationText = `just started`;
                  }
                }

                return (
                  <tr
                    key={c.id}
                    style={{
                      background: isSaved ? '#f0fdf4' : selected.has(step.id) ? 'var(--crm-primary-light)' : '',
                      transition: 'background 0.2s'
                    }}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(step.id)}
                        onChange={() => toggleSelect(step.id)}
                      />
                    </td>
                    <td>
                      <div style={{ fontWeight: 700, fontSize: '14px', display: 'flex', alignItems: 'center' }}>
                         {isSlaBreached && (
                           <span 
                             style={{
                               display: 'inline-block',
                               width: '8px',
                               height: '8px',
                               borderRadius: '50%',
                               backgroundColor: '#EF4444',
                               marginRight: '8px',
                               flexShrink: 0,
                               animation: 'crm-pulse 1.8s infinite ease-in-out'
                             }}
                             title="SLA Breach: Stuck for 3+ days!"
                           />
                         )}
                         {c.customer_name}
                      </div>
                      <div style={{ fontSize: '11.5px', color: 'var(--crm-text-muted)', marginTop: 2 }}>#{c.id.slice(0, 8)}</div>
                    </td>
                    <td>{c.phone}</td>
                    <td style={{ fontSize: '13px', color: 'var(--crm-text-secondary)' }}>{c.car_details || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                        <span style={{
                          padding: '4px 10px', borderRadius: '20px', fontSize: '11.5px', fontWeight: 700,
                          background: statusColors[step.status], color: statusText[step.status], display: 'inline-block'
                        }}>
                          {step.status === 'Done' ? '✅ Done' : step.status === 'Hold' ? '🔴 Hold' : '⏳ Pending'}
                        </span>
                        {durationText && (
                          <span style={{ fontSize: '10.5px', color: '#64748B', fontWeight: 650, marginLeft: '4px' }}>
                            ({durationText})
                          </span>
                        )}
                      </div>
                    </td>
                    {/* Remarks / Tasks column */}
                    <td style={{ maxWidth: 160 }}>
                      {step.reason ? (
                        <div style={{ 
                          fontSize: '11.5px', color: '#991B1B', fontWeight: 600, 
                          background: '#FEF2F2', padding: '4px 8px', borderRadius: '6px',
                          border: '1px solid #FECACA', lineHeight: 1.4, maxWidth: 150
                        }}>
                          ⚠️ {step.reason}
                        </div>
                      ) : (
                        <span style={{ fontSize: '11px', color: 'var(--crm-text-muted)', fontStyle: 'italic' }}>No remarks</span>
                      )}
                    </td>
                    <td style={{ fontSize: '12px', color: 'var(--crm-text-muted)' }}>
                      {step.updated_at ? new Date(step.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}
                      {step.updated_by?.name && <div style={{ fontSize: '11px' }}>by {step.updated_by.name}</div>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {['Done', 'Hold', 'Pending'].map(s => (
                          <button
                            key={s}
                            onClick={() => openUpdateModal(step, c)}
                            style={{
                              padding: '4px 9px', fontSize: '11.5px', borderRadius: '5px', fontWeight: 700, cursor: 'pointer',
                              background: step.status === s ? statusColors[s] : 'white',
                              color: step.status === s ? statusText[s] : 'var(--crm-text-muted)',
                              border: `1.5px solid ${step.status === s ? statusText[s] : 'var(--crm-border)'}`,
                              transition: 'all 0.15s'
                            }}
                          >
                            {s === 'Done' ? '✓' : s === 'Hold' ? '⏸' : '⏳'} {s}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button 
                          onClick={() => setChecklistModalStep(step)}
                          style={{
                            padding: '6px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                            background: step.step_name === 'Document Complete' ? '#EEF2FF' : '#F0F9FF',
                            color: step.step_name === 'Document Complete' ? '#4F46E5' : '#0369A1',
                            border: `1.5px solid ${step.step_name === 'Document Complete' ? '#C7D2FE' : '#BAE6FD'}`,
                            borderRadius: '8px', transition: 'all 0.2s', whiteSpace: 'nowrap'
                          }}
                          title="Manage sub-tasks / customer document checklist"
                        >
                          {step.step_name === 'Document Complete' ? '📄 Documents Checklist' : '📋 Tasks'}
                        </button>
                        <button 
                          onClick={() => setSelectedCustomerMessages(c)}
                          style={{ 
                            position: 'relative',
                            padding: '6px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                            background: step.reason ? '#FEF2F2' : 'var(--crm-primary-light)', 
                            color: step.reason ? '#991B1B' : 'var(--crm-primary)', 
                            border: `1.5px solid ${step.reason ? '#FECACA' : 'var(--crm-primary)'}`,
                            borderRadius: '8px',
                            transition: 'all 0.2s',
                            whiteSpace: 'nowrap'
                          }}
                          title="View / add notes and tasks for this customer"
                        >
                          💬 Notes
                          {step.reason && (
                            <span style={{ 
                              position: 'absolute', top: -5, right: -5,
                              background: '#EF4444', color: 'white',
                              width: 16, height: 16, borderRadius: '50%',
                              fontSize: '9px', fontWeight: 800,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              border: '2px solid white'
                            }}>!</span>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Messages Modal */}
      {selectedCustomerMessages && (
        <CustomerMessages 
          customer={selectedCustomerMessages} 
          userRole={userRole} 
          onClose={() => setSelectedCustomerMessages(null)} 
        />
      )}

      {/* Single Status Update Modal */}
      {activeUpdateModal && (
        <div className="crm-modal-overlay" onClick={() => setActiveUpdateModal(null)}>
          <div className="crm-modal" style={{ maxWidth: '480px', width: '90%' }} onClick={e => e.stopPropagation()}>
            <div className="crm-modal-header">
              <h3>Update Status: {activeUpdateModal.stepName}</h3>
              <button className="crm-modal-close" onClick={() => setActiveUpdateModal(null)}>✕</button>
            </div>
            <div className="crm-modal-body">
              <div style={{ marginBottom: '14px', fontSize: '13.5px', color: 'var(--crm-text-secondary)' }}>
                Updating workflow status for customer <strong>{activeUpdateModal.customerName}</strong>
              </div>
              <div className="crm-modal-row">
                <label>Status</label>
                <select
                  value={modalStatus}
                  onChange={e => setModalStatus(e.target.value)}
                  style={{ padding: '8px 12px', border: '1.5px solid var(--crm-border)', borderRadius: '6px', fontSize: '13.5px', width: '100%', outline: 'none' }}
                >
                  <option value="Pending">⏳ Pending</option>
                  <option value="Hold">🔴 Hold</option>
                  <option value="Done">✅ Done</option>
                  <option value="Cancelled">❌ Booking Cancelled</option>
                </select>
              </div>
              {(modalStatus === 'Hold' || modalStatus === 'Pending' || modalStatus === 'Cancelled') && (
                <div className="crm-modal-row" style={{ marginTop: '14px' }}>
                  <label>{modalStatus === 'Cancelled' ? 'Cancellation Reason' : 'Reason / Remarks'}</label>
                  <textarea
                    placeholder={modalStatus === 'Cancelled' ? "Enter reason for booking cancellation..." : "Enter reason for hold or status update..."}
                    rows={3}
                    value={modalReason}
                    onChange={e => setModalReason(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1.5px solid var(--crm-border)', borderRadius: '6px', fontSize: '13.5px', outline: 'none', resize: 'vertical' }}
                  />
                </div>
              )}
            </div>
            <div className="crm-modal-actions" style={{ borderTop: '1px solid var(--crm-border)', paddingTop: '14px', marginTop: '14px' }}>
              <button className="crm-btn crm-btn-ghost" onClick={() => setActiveUpdateModal(null)}>Cancel</button>
              <button 
                className="crm-btn crm-btn-success" 
                onClick={handleModalSubmit}
                disabled={saving === activeUpdateModal.stepId}
              >
                {saving === activeUpdateModal.stepId ? 'Saving…' : '✓ Save Status'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checklist Tasks Modal */}
      {checklistModalStep && (
        <div className="crm-modal-overlay" onClick={() => setChecklistModalStep(null)}>
          <div className="crm-modal" style={{ maxWidth: '520px', width: '90%' }} onClick={e => e.stopPropagation()}>
            <div className="crm-modal-header">
              <h3>{checklistModalStep.step_name === 'Document Complete' ? '📄 Customer Documents Checklist' : `📋 Checklist: ${checklistModalStep.step_name}`}</h3>
              <button className="crm-modal-close" onClick={() => setChecklistModalStep(null)}>✕</button>
            </div>
            <div className="crm-modal-body">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <label style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--crm-text-secondary)', display: 'block', margin: 0 }}>
                  {checklistModalStep.step_name === 'Document Complete' ? '📝 Mark/Tick Received Customer Documents' : '📝 Sub-Tasks checklist for this customer'}
                </label>
                {checklists.length === 0 && (
                  <button
                    type="button"
                    onClick={handleLoadTemplate}
                    disabled={loadingTemplate}
                    style={{
                      fontSize: '11px',
                      color: '#4F46E5',
                      background: '#EEF2FF',
                      border: '1px solid #C7D2FE',
                      borderRadius: '4px',
                      padding: '3px 8px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                    title="Automatically load default operational checklist tasks for this stage"
                  >
                    {loadingTemplate ? 'Loading...' : '⚡ Auto-Load Template'}
                  </button>
                )}
              </div>

              {checklists.length === 0 ? (
                <p style={{ fontSize: '12.5px', color: 'var(--crm-text-muted)', margin: '0 0 16px' }}>
                  No checklist tasks defined yet. Click "Auto-Load Template" or write a task below.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8.5px', marginBottom: '16px', background: '#F8FAFC', padding: '12px 14px', borderRadius: '8px', border: '1px solid #F1F5F9', maxHeight: '250px', overflowY: 'auto' }}>
                  {checklists.map((item) => (
                    <label key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', cursor: 'pointer', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        checked={item.is_completed}
                        onChange={() => handleToggleChecklist(item.id, item.is_completed)}
                      />
                      <span style={{ 
                        textDecoration: item.is_completed ? 'line-through' : 'none', 
                        color: item.is_completed ? 'var(--crm-text-muted)' : 'var(--crm-text-primary)',
                        fontWeight: item.is_completed ? 400 : 500
                      }}>
                        {item.task_name}
                      </span>
                    </label>
                  ))}
                </div>
              )}

              {/* Add checklist item */}
              <div style={{ display: 'flex', gap: '6px', borderTop: '1px solid var(--crm-border)', paddingTop: '14px' }}>
                <input
                  type="text"
                  placeholder="Type a new sub-task check..."
                  value={newChecklistTask}
                  onChange={e => setNewChecklistTask(e.target.value)}
                  style={{ 
                    flex: 1, 
                    padding: '8px 12px', 
                    fontSize: '13px', 
                    border: '1.5px solid var(--crm-border)', 
                    borderRadius: '6px', 
                    outline: 'none',
                    background: 'white'
                  }}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddChecklist(); } }}
                />
                <button
                  type="button"
                  onClick={handleAddChecklist}
                  style={{ 
                    padding: '7px 16px', 
                    fontSize: '13px', 
                    background: 'var(--crm-primary)', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '6px', 
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  + Add
                </button>
              </div>
            </div>
            <div className="crm-modal-actions" style={{ borderTop: '1px solid var(--crm-border)', paddingTop: '14px', marginTop: '14px' }}>
              <button className="crm-btn crm-btn-ghost" onClick={() => setChecklistModalStep(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentDashboard;
